import { useCallback } from 'react'

import { getOS, isWebView } from '../userAgent/utils'

export const AUTO_TIMEOUT_DURATION = 1000

export interface useCheckMicrophonePermissionReturnType {
  checkPermissionStatus: () => Promise<PermissionState | 'unknown'>
  initiateMicrophoneAccess: (
    constraints?: MediaStreamConstraints,
  ) => Promise<MediaStream | void> | undefined
}

/**
 * Checks for microphone permission status in a browser.
 *
 * The hook provides a method that, when called, attempts to access the user's microphone
 * to determine the permission status. It handles three different scenarios: when permission
 * is granted, when permission is rejected, and when the permission prompt is dismissed
 * and another method that checks the current permission status.
 *
 * A timer is set to differentiate between automatic permission (based on previous interaction)
 * and manual permission, which is determined by the user's action after the timer expires.
 */
export function useCheckMicrophonePermission({
  onAllowed,
  onRejected,
  onDismissed,
}: {
  onAllowed?: (method: 'manual' | 'auto') => void
  onRejected?: (method: 'manual' | 'auto') => void
  onDismissed?: () => void
}): useCheckMicrophonePermissionReturnType {
  const checkPermissionStatus = useCallback(async () => {
    const checkPermissionsFallback = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        })
        // Immediately stop using the stream if it was successfully acquired.
        stream.getTracks().forEach((track) => track.stop())
        return 'granted'
      } catch (error) {
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'PermissionDeniedError'
        ) {
          return 'denied'
        } else {
          return 'unknown'
        }
      }
    }

    // If in Android WebView, directly use the fallback approach since permissions API isn't reliable in Android WebView
    if (isWebView() && getOS() === 'Android') {
      return checkPermissionsFallback()
    }

    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        })
        return permissionStatus.state // 'granted', 'denied', or 'prompt'
      } catch (error) {
        return checkPermissionsFallback()
      }
    } else {
      // Attempt to use getUserMedia as a fallback to infer permission status.
      return checkPermissionsFallback()
    }
  }, [])

  const initiateMicrophoneAccess = useCallback(
    (constraints: MediaStreamConstraints = { audio: true }) => {
      let timeoutPassed = false
      setTimeout(() => {
        // Once 1 second has passed, assume an allow or reject action was manually
        // done by the user, instead of the system returning a previous permission
        // value.
        timeoutPassed = true
      }, AUTO_TIMEOUT_DURATION)

      if (
        navigator?.mediaDevices?.getUserMedia &&
        typeof navigator.mediaDevices.getUserMedia === 'function'
      ) {
        return navigator.mediaDevices
          .getUserMedia(constraints)
          .then((stream) => {
            if (stream.active) {
              onAllowed?.(timeoutPassed ? 'manual' : 'auto')
            }
            return stream
          })
          .catch((error) => {
            if (
              error.message === 'Permission denied' ||
              // for Safari
              error.name === 'NotAllowedError'
            ) {
              onRejected?.(timeoutPassed ? 'manual' : 'auto')
            }
            if (error.message === 'Permission dismissed') {
              onDismissed?.()
            }
          })
      }
    },
    [onAllowed, onRejected, onDismissed],
  )

  return { checkPermissionStatus, initiateMicrophoneAccess }
}
